import { FFmpeg } from '@ffmpeg/ffmpeg';
import { toBlobURL } from '@ffmpeg/util';

// `@ffmpeg/ffmpeg` publishes its `FFFSType` string enum on a subpath the
// package's `exports` map doesn't expose, so importing it as a value fails
// under bundler resolution. The enum's underlying string values are stable,
// so `ffmpeg.mount` is called with the raw string and a cast.
type FFFSType = 'MEMFS' | 'NODEFS' | 'NODERAWFS' | 'IDBFS' | 'WORKERFS' | 'PROXYFS';
const FFFSType = { WORKERFS: 'WORKERFS' as FFFSType };

export type LogHandler = (message: string) => void;
export type ProgressHandler = (fraction: number) => void;

/** Handle to a mounted input file; call `unmount` when the file is no longer
 *  needed so the WORKERFS mount is torn down cleanly. */
export interface InputMount {
  /** Path ffmpeg args should reference (e.g. `/input/input.mp4`). */
  path: string;
  unmount: () => Promise<void>;
}

let instance: FFmpeg | null = null;
let loadPromise: Promise<FFmpeg> | null = null;

/**
 * Lazily loads the LGPL, single-threaded ffmpeg-core from `/ffmpeg-core/`.
 * The core JS is fetched and served back as a Blob URL so Vite's dev-server
 * module transform never sees a dynamic-import request for the raw file
 * (which would 500 as Vite tries to parse the emscripten output as ESM).
 * Self-hosted, so no CDN dependency.
 *
 * The multi-threaded build (`/ffmpeg-core-mt/`) is deployed alongside but
 * not loaded by default. Empirically, MT hurts more than it helps on Wave's
 * workload: audio extract is a single short call (~1s) where thread-spawn
 * overhead dominates, and Path B fires many short segment calls that each
 * pay the same overhead. On a 5.77 s VP9 test the MT build ran the pipeline
 * in 34 s vs 9 s for ST; on a 13.35 s WMV Path B run, 34 s vs 25 s. MT would
 * pay off in a pattern with one long ffmpeg call — Wave doesn't have that
 * pattern today. The MT assets are shipped so a future refactor can opt in
 * per-job (e.g. one giant decode-into-fragmented-mp4 pass) without another
 * deploy. See `project-transcode-backend`.
 */
export function loadEngine(): Promise<FFmpeg> {
  if (instance) return Promise.resolve(instance);
  if (loadPromise) return loadPromise;

  const ffmpeg = new FFmpeg();
  loadPromise = (async () => {
    const [coreURL, wasmURL] = await Promise.all([
      toBlobURL('/ffmpeg-core/ffmpeg-core.js', 'text/javascript'),
      toBlobURL('/ffmpeg-core/ffmpeg-core.wasm', 'application/wasm'),
    ]);
    await ffmpeg.load({ coreURL, wasmURL });
    instance = ffmpeg;
    return ffmpeg;
  })();

  return loadPromise;
}

export interface RunFFmpegOptions {
  onLog?: LogHandler;
  onProgress?: ProgressHandler;
  /**
   * Source duration in seconds. When set, `runFFmpeg` parses `time=` from
   * ffmpeg's log lines and reports progress = time / duration, so the UI still
   * moves for encoders that don't emit native progress events (some rare
   * codec configurations in the wasm build stay silent). The native event
   * still wins when present and monotonic.
   */
  durationSeconds?: number | null;
  /** Abort the ffmpeg exec cooperatively; the worker keeps running and can
   *  start the next job immediately (no wasm re-warm). */
  signal?: AbortSignal;
}

/**
 * Runs the ffmpeg command, buffering all stderr log lines and returning them
 * alongside the exit code. Progress and log handlers are attached only for the
 * duration of this call and removed afterwards.
 */
export async function runFFmpeg(
  args: string[],
  opts: RunFFmpegOptions = {}
): Promise<{ code: number; logs: string[] }> {
  const ffmpeg = await loadEngine();
  const logs: string[] = [];
  // Progress is reported to the caller as the maximum of the native event and
  // the log-derived value, and never decreases — either source alone can jitter
  // slightly on the wasm build.
  let lastProgress = 0;
  const report = (fraction: number): void => {
    if (!isFinite(fraction)) return;
    const clamped = Math.max(0, Math.min(1, fraction));
    if (clamped <= lastProgress) return;
    lastProgress = clamped;
    opts.onProgress?.(clamped);
  };

  const durationSeconds = opts.durationSeconds ?? null;
  // Matches `time=00:01:23.45` (ffmpeg progress line) and `time=1234.56`
  // (some newer builds print seconds directly).
  const TIME_HMS = /time=(\d+):(\d{2}):(\d{2}(?:\.\d+)?)/;
  const TIME_SEC = /time=(\d+(?:\.\d+)?)(?:\s|$)/;

  const logListener = ({ message }: { message: string }): void => {
    logs.push(message);
    opts.onLog?.(message);
    if (!durationSeconds || durationSeconds <= 0) return;
    const hms = message.match(TIME_HMS);
    let seconds: number | null = null;
    if (hms) {
      seconds = Number(hms[1]) * 3600 + Number(hms[2]) * 60 + Number(hms[3]);
    } else {
      const sec = message.match(TIME_SEC);
      if (sec) seconds = Number(sec[1]);
    }
    if (seconds != null) report(seconds / durationSeconds);
  };
  const progressListener = ({ progress }: { progress: number }): void => {
    report(progress);
  };

  ffmpeg.on('log', logListener);
  ffmpeg.on('progress', progressListener);

  try {
    const code = await ffmpeg.exec(args, undefined, { signal: opts.signal });
    return { code, logs };
  } finally {
    ffmpeg.off('log', logListener);
    ffmpeg.off('progress', progressListener);
  }
}

export async function writeFile(name: string, data: Uint8Array): Promise<void> {
  const ffmpeg = await loadEngine();
  await ffmpeg.writeFile(name, data);
}

export async function readFile(name: string): Promise<Uint8Array> {
  const ffmpeg = await loadEngine();
  const data = await ffmpeg.readFile(name);
  if (typeof data === 'string') {
    throw new Error(`Expected binary output for ${name}, got string`);
  }
  return data;
}

export async function deleteFile(name: string): Promise<void> {
  const ffmpeg = await loadEngine();
  try {
    await ffmpeg.deleteFile(name);
  } catch {
    // best-effort cleanup
  }
}

/**
 * Mounts a browser File into ffmpeg's virtual filesystem via WORKERFS. The
 * file's bytes stay in the browser's Blob storage — WORKERFS reads them on
 * demand instead of copying them into the wasm heap. That's roughly half the
 * peak memory of the writeFile path for large inputs.
 *
 * If the browser refuses (rare — usually old iOS Safari, or a File that lost
 * its underlying blob after a page navigation), falls back to writeFile so the
 * conversion still works, just with the higher memory cost.
 *
 * Caller must invoke the returned `unmount()` after the ffmpeg run finishes.
 */
export async function mountInputFile(
  file: File,
  ffmpegName: string
): Promise<InputMount> {
  const ffmpeg = await loadEngine();
  const mountPoint = '/input';
  try {
    // Ensure the mount directory exists. `createDir` may throw if it already
    // exists from a previous run; that's fine.
    try { await ffmpeg.createDir(mountPoint); } catch { /* already exists */ }
    await ffmpeg.mount(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      FFFSType.WORKERFS as any,
      { blobs: [{ name: ffmpegName, data: file }] },
      mountPoint
    );
    const path = `${mountPoint}/${ffmpegName}`;
    return {
      path,
      unmount: async () => {
        try { await ffmpeg.unmount(mountPoint); } catch { /* best-effort */ }
      },
    };
  } catch {
    // Fallback: copy bytes into wasm heap the old way.
    const bytes = new Uint8Array(await file.arrayBuffer());
    await ffmpeg.writeFile(ffmpegName, bytes);
    return {
      path: ffmpegName,
      unmount: async () => {
        try { await ffmpeg.deleteFile(ffmpegName); } catch { /* best-effort */ }
      },
    };
  }
}

/**
 * Cancels any running job by killing the ffmpeg worker.
 * The next call to any engine function will transparently re-load the core.
 */
export function cancelEngine(): void {
  const current = instance;
  instance = null;
  loadPromise = null;
  if (current) {
    try {
      current.terminate();
    } catch {
      // ignore
    }
  }
}

export class CancelledError extends Error {
  constructor() {
    super('Conversion cancelled');
    this.name = 'CancelledError';
  }
}
